import { useFeatureFlagVariantKey } from 'posthog-js/react';

/**
 * Which implementation serves the cage lice series:
 *
 * - `sql` — `LiceCalculationService` over `lice.sql`, what production serves today.
 * - `core` — the replacement: raw rows from the database, with the averages, the rolling
 *   window and the ISO-week bucketing done in the pure modules under `calculations/lice/core/`.
 * - `compare` — runs both and serves the `core` payload with a field-by-field diff attached.
 *   Never cached and costs two payload builds, so it is the rung for validating a cohort, not
 *   one to leave a population on.
 *
 * The same three names as `useCageOverviewSource`, deliberately: it is the same manoeuvre.
 */
const SOURCES = ['sql', 'core', 'compare'] as const;
export type LiceSource = (typeof SOURCES)[number];

function isSource(value: unknown): value is LiceSource {
    return SOURCES.some((source) => source === value);
}

/**
 * Reads the `lice-source` PostHog flag, a **multivariate** flag whose variants are the three
 * source names. It is the dial for moving traffic onto the rebuilt calculation one cohort at a
 * time, and turning it off is the kill switch.
 *
 * PostHog is the only input, deliberately: the sessionStorage override in `FeatureFlagsContext`
 * is boolean, so it could only ever force one arbitrary variant. To try a variant locally, set
 * yourself as a release condition on the flag.
 *
 * Everything that is not an explicitly recognised variant resolves to `'sql'`: flags still
 * loading, PostHog unreachable, the flag disabled (`useFeatureFlagVariantKey` returns `false`
 * there), and a variant renamed in the dashboard. Every degraded path therefore lands on what
 * production serves today, and a typo in PostHog cannot send the backend a value it would
 * reject with a 400.
 *
 * Not blocking on the flag has a known consequence: on a cold load a flipped user fetches once
 * with `sql` and again once flags arrive, since `source` is part of the react-query key. That
 * is accepted — `posthog-js` caches flags in localStorage after the first visit, and the two
 * implementations are meant to return the same numbers. Waiting for the flag instead would let
 * an adblocker or a PostHog outage stop the lice pages loading at all.
 */
export function useLiceSource(): LiceSource {
    const variant = useFeatureFlagVariantKey('lice-source');

    return isSource(variant) ? variant : 'sql';
}
